import React, { useId } from 'react';
import {
  ACCESSORIES,
  BACKGROUNDS,
  CLOTHING,
  EYEBROWS,
  EYES,
  FACE_DETAILS,
  FACIAL_HAIR,
  GLASSES,
  HAIR_COLORS,
  HAIR_STYLES,
  MOUTHS,
  OUTFIT_COLORS,
  SKIN_COLORS,
  LASHES,
  LIPSTICK,
  EARRINGS,
  EYE_COLORS,
  DEFAULT_AVATAR,
  normalizeAvatar,
  type AvatarConfig,
} from '../../shared/avatar';

export { DEFAULT_AVATAR };

type SizeToken = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'fill';

interface AvatarProps {
  /** Admite cualquier cosa: un avatar guardado antiguo, parcial o vacío se completa. */
  config: unknown;
  /** Token de tamaño, o píxeles exactos cuando el diseño lo pide (podio, rejillas). */
  size?: SizeToken | number;
  className?: string;
  animate?: boolean;
  /**
   * `face` encuadra solo la cara. Lo usa el taller para las miniaturas de ojos,
   * boca o gafas: a 56 px, con el cuerpo entero, esos detalles no se distinguen.
   */
  crop?: 'full' | 'face';
}

const SIZE_MAP = {
  xs: 'w-7 h-7',
  sm: 'w-10 h-10',
  md: 'w-14 h-14',
  lg: 'w-20 h-20',
  xl: 'w-28 h-28',
  '2xl': 'w-36 h-36',
  // Ocupa el ancho de su contenedor: las miniaturas del taller en rejilla.
  fill: 'w-full aspect-square',
};

/* ------------------------------------------------------------------ *
 * Color
 * ------------------------------------------------------------------ */

/** Aclara (amount > 0) u oscurece (amount < 0) un color hexadecimal. */
function shade(hex: string, amount: number): string {
  const n = parseInt(hex.slice(1), 16);
  const target = amount < 0 ? 0 : 255;
  const t = Math.abs(amount);
  const mix = (c: number) => Math.round(c + (target - c) * t);
  const r = mix((n >> 16) & 255);
  const g = mix((n >> 8) & 255);
  const b = mix(n & 255);
  return `#${((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1)}`;
}

/** ¿Es un color claro? Decide si los trazos encima van oscuros o claros. */
function isLight(hex: string): boolean {
  const n = parseInt(hex.slice(1), 16);
  const lum = 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
  return lum > 170;
}

const INK = '#1B1F2A';
const INK_LINE = INK;

/* ------------------------------------------------------------------ *
 * Geometría
 *
 * Lienzo de 100 × 100. La cabeza es una elipse centrada en (50, 47) de
 * 20 × 22, así que va de x 30 a 70 y de y 25 a 69. Los ojos caen en
 * y 48, la boca en y 59 y los hombros arrancan en y 76.
 * ------------------------------------------------------------------ */

const HATS = new Set(['cap', 'beanie', 'wizard', 'viking', 'graduation', 'headwrap']);

/** Con el hiyab no asoma el pelo ni las orejas: se tapan, no se dibujan. */
const COVERS_HAIR = new Set(['hijab']);

/** Miradas sin párpado donde unas pestañas no tienen dónde ir. */
const NO_LID_EYES = new Set(['stars', 'hearts']);

export const Avatar: React.FC<AvatarProps> = ({
  config,
  size = 'md',
  className = '',
  animate = false,
  crop = 'full',
}) => {
  const a = normalizeAvatar(config);
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const bg = BACKGROUNDS.find((b) => b.id === a.background) ?? BACKGROUNDS[0];

  const numeric = typeof size === 'number';
  const sizeClass = numeric ? '' : SIZE_MAP[size];
  const viewBox = crop === 'face' ? '27 29 46 46' : '0 0 100 100';

  const skin = a.skinColor;
  const skinShadow = shade(skin, -0.16);
  const hair = a.hairColor;
  const hat = HATS.has(a.accessory);
  const covered = COVERS_HAIR.has(a.accessory);
  const lip = LIPSTICK.find((l) => l.id === a.lipstick)?.color || null;

  return (
    <div
      style={numeric ? { width: size, height: size } : undefined}
      className={`relative inline-flex items-center justify-center shrink-0 select-none overflow-hidden rounded-[28%] ring-1 ring-black/10 shadow-sm ${sizeClass} ${animate ? 'hover:scale-105 transition-transform duration-300' : ''} ${className}`}
    >
      <svg viewBox={viewBox} className="w-full h-full block" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <defs>
          <linearGradient id={`bg-${uid}`} x1="0" y1="0" x2="0.4" y2="1">
            <stop offset="0" stopColor={bg.from} />
            <stop offset="1" stopColor={bg.to} />
          </linearGradient>
        </defs>

        <rect x="0" y="0" width="100" height="100" fill={`url(#bg-${uid})`} />
        <circle cx="50" cy="44" r="34" fill="#FFFFFF" opacity="0.14" />

        {!covered && <HairBack style={a.hairStyle} color={hair} />}
        {a.accessory === 'halo' && <Halo back />}

        <Body kind={a.clothing} color={a.clothingColor} skin={skin} skinShadow={skinShadow} />

        {/* Orejas */}
        <ellipse cx="30.5" cy="49" rx="4.2" ry="5.4" fill={skin} />
        <ellipse cx="69.5" cy="49" rx="4.2" ry="5.4" fill={skin} />
        <ellipse cx="30.8" cy="49.4" rx="2" ry="3" fill={skinShadow} />
        <ellipse cx="69.2" cy="49.4" rx="2" ry="3" fill={skinShadow} />

        {/* Cabeza */}
        <ellipse cx="50" cy="47" rx="20" ry="22" fill={skin} />
        <path d="M34 60 C38 67 44 69 50 69 C56 69 62 67 66 60 C62 65 56 67 50 67 C44 67 38 65 34 60 Z" fill={skinShadow} opacity="0.45" />

        <Detail kind={a.detail} skin={skin} accent={a.accessoryColor} />
        <FacialHair kind={a.facialHair} color={hair} />
        <Eyebrows kind={a.eyebrows} color={a.hairStyle === 'bald' ? shade(skin, -0.45) : shade(hair, -0.15)} eyes={a.eyes} />
        <Eyes kind={a.eyes} iris={a.eyeColor} />
        {!NO_LID_EYES.has(a.eyes) && <Lashes kind={a.lashes} eyes={a.eyes} />}
        {/* Nariz */}
        <path d="M48.6 51.5 Q50 54.2 52 52.6" fill="none" stroke={shade(skin, -0.32)} strokeWidth="1.3" strokeLinecap="round" />
        <Mouth kind={a.mouth} lip={lip} />
        <Glasses kind={a.glasses} />

        {!covered && <Earrings kind={a.earrings} color={a.accessoryColor} />}
        {!covered && <HairFront style={a.hairStyle} color={hair} hat={hat} />}
        <Accessory kind={a.accessory} color={a.accessoryColor} hair={hair} />
      </svg>
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * Pelo
 * ------------------------------------------------------------------ */

const HairBack: React.FC<{ style: string; color: string }> = ({ style, color }) => {
  const dark = shade(color, -0.18);
  switch (style) {
    case 'afro':
      // Volumen de verdad: una nube ancha con el borde ondulado, no un casco.
      return (
        <g fill={color}>
          <circle cx="50" cy="37" r="30" />
          {Array.from({ length: 14 }, (_, i) => {
            const t = (Math.PI * 2 * i) / 14 - Math.PI / 2;
            return <circle key={i} cx={50 + Math.cos(t) * 29} cy={37 + Math.sin(t) * 29} r="6.4" />;
          })}
          <circle cx="24" cy="54" r="10" />
          <circle cx="76" cy="54" r="10" />
        </g>
      );
    case 'bob':
      return <path d="M28 44 C26 26 38 19 50 19 C62 19 74 26 72 44 L74 64 C68 67 62 66 60 64 L40 64 C38 66 32 67 26 64 Z" fill={dark} />;
    case 'long':
      return <path d="M28 42 C25 24 38 18 50 18 C62 18 75 24 72 42 L76 90 C70 94 62 92 60 88 L40 88 C38 92 30 94 24 90 Z" fill={dark} />;
    case 'wavy':
      return (
        <path
          d="M28 42 C25 24 38 18 50 18 C62 18 75 24 72 42 C76 50 72 56 76 62 C80 68 74 74 76 80 C70 84 64 80 62 76 L38 76 C36 80 30 84 24 80 C26 74 20 68 24 62 C28 56 24 50 28 42 Z"
          fill={dark}
        />
      );
    case 'ponytail':
      return (
        <g>
          <path d="M64 30 C80 28 86 44 82 62 C80 72 74 78 70 76 C76 64 76 46 66 38 Z" fill={dark} />
          <circle cx="68" cy="33" r="3.2" fill={shade(color, 0.35)} />
        </g>
      );
    case 'pigtails':
      return (
        <g fill={dark}>
          <ellipse cx="22" cy="56" rx="7" ry="13" />
          <ellipse cx="78" cy="56" rx="7" ry="13" />
          <circle cx="26" cy="42" r="3" fill={shade(color, 0.35)} />
          <circle cx="74" cy="42" r="3" fill={shade(color, 0.35)} />
        </g>
      );
    case 'braids':
      return (
        <g fill={dark}>
          <path d="M28 42 C25 24 38 18 50 18 C62 18 75 24 72 42 L72 58 L28 58 Z" />
          {[60, 67, 74, 81].map((y) => (
            <React.Fragment key={y}>
              <ellipse cx="28" cy={y} rx="4.2" ry="4" />
              <ellipse cx="72" cy={y} rx="4.2" ry="4" />
            </React.Fragment>
          ))}
          <path d="M26 85 L30 85 L28 91 Z M70 85 L74 85 L72 91 Z" fill={shade(color, 0.35)} />
        </g>
      );
    case 'puffs':
      return (
        <g fill={color}>
          {[
            [27, 27, 9],
            [73, 27, 9],
            [22, 22, 4],
            [32, 18, 4],
            [68, 18, 4],
            [78, 22, 4],
          ].map(([cx, cy, r]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
          ))}
        </g>
      );
    case 'locs':
      return (
        <g fill={dark}>
          <path d="M29 42 C27 26 38 19 50 19 C62 19 73 26 71 42 L71 52 L29 52 Z" />
          {[
            [23.6, 44],
            [27.8, 50],
            [32, 42],
            [63.8, 42],
            [68, 50],
            [72.2, 44],
          ].map(([x, h]) => (
            <rect key={x} x={x} y="36" width="4.4" height={h} rx="2.2" />
          ))}
          {[25.8, 30, 34.2, 66, 70.2, 74.4].map((x) => (
            <path key={x} d={`M${x - 1.6} 60 L${x + 1.6} 60 M${x - 1.6} 70 L${x + 1.6} 70`} stroke={shade(color, 0.2)} strokeWidth="0.8" />
          ))}
        </g>
      );
    case 'long_curly':
      return (
        <g fill={dark}>
          <path d="M28 42 C25 24 38 18 50 18 C62 18 75 24 72 42 L75 84 L25 84 Z" />
          {[38, 46, 54, 62, 70, 78, 85].map((y, i) => (
            <React.Fragment key={y}>
              <circle cx={i % 2 ? 24 : 26} cy={y} r="6" />
              <circle cx={i % 2 ? 76 : 74} cy={y} r="6" />
            </React.Fragment>
          ))}
          {[30, 38, 46, 54, 62, 70].map((x) => (
            <circle key={x} cx={x} cy="88" r="5" />
          ))}
        </g>
      );
    case 'bangs':
      return <path d="M28 42 C25 24 38 18 50 18 C62 18 75 24 72 42 L75 86 C70 90 62 88 60 84 L40 84 C38 88 30 90 25 86 Z" fill={dark} />;
    case 'cornrows':
      return (
        <g fill={dark}>
          <ellipse cx="50" cy="73" rx="4" ry="5" />
          <ellipse cx="50" cy="81" rx="3.4" ry="4.2" />
        </g>
      );
    case 'curly':
      return (
        <g fill={dark}>
          <circle cx="29" cy="46" r="6" />
          <circle cx="71" cy="46" r="6" />
          <circle cx="30" cy="55" r="5" />
          <circle cx="70" cy="55" r="5" />
        </g>
      );
    default:
      return null;
  }
};

const HairFront: React.FC<{ style: string; color: string; hat: boolean }> = ({ style, color, hat }) => {
  const light = shade(color, 0.25);
  const shine = (d: string) => <path d={d} fill="none" stroke={light} strokeWidth="1.6" strokeLinecap="round" opacity="0.7" />;
  switch (style) {
    case 'buzz':
      return <path d="M30.5 42 C30 30 39 24.6 50 24.6 C61 24.6 70 30 69.5 42 C66 35 59 32 50 32 C41 32 34 35 30.5 42 Z" fill={color} opacity="0.85" />;
    case 'short':
      return (
        <g>
          <path d="M29 45 C27 28 38 21 50 21 C62 21 73 28 71 45 C69 38 66 34 62 32.5 C55 35.5 44 35.5 37 32.5 C33 35 31 39 29 45 Z" fill={color} />
          {shine('M40 26 C44 24 50 23.6 55 24.4')}
        </g>
      );
    case 'sidepart':
      return (
        <g>
          <path d="M29 46 C26 28 38 20 52 20 C64 20 74 28 71 45 C70 38 68 34 65 32 C58 33 46 31 41 27 C39 33 34 38 29 46 Z" fill={color} />
          <path d="M41 27 C40 24 42 22 44 21.4" stroke={shade(color, -0.3)} strokeWidth="1" fill="none" />
          {shine('M50 23.4 C56 22.8 62 24 66 27')}
        </g>
      );
    case 'spiky':
      return (
        <path d="M28 44 L29 30 L35 33 L37 21 L44 28 L49 16 L54 27 L61 19 L63 30 L70 27 L72 44 C69 37 62 33.5 50 33 C38 33.5 31 37 28 44 Z" fill={color} />
      );
    case 'mohawk':
      return (
        <g>
          <path d="M30.5 42 C30 30 39 24.6 50 24.6 C61 24.6 70 30 69.5 42 C66 35 59 32 50 32 C41 32 34 35 30.5 42 Z" fill={color} opacity="0.4" />
          {!hat && <path d="M44 32 L41 18 L46 21 L47 10 L51 19 L54 9 L56 20 L60 16 L57 32 Z" fill={color} />}
        </g>
      );
    case 'curly':
      return (
        <g fill={color}>
          {[
            [33, 30, 7],
            [41, 24, 7.5],
            [50, 22, 8],
            [59, 24, 7.5],
            [67, 30, 7],
            [30, 38, 5.5],
            [70, 38, 5.5],
            [45, 31, 5],
            [55, 31, 5],
          ].map(([cx, cy, r]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
          ))}
          <circle cx="47" cy="20" r="2" fill={light} opacity="0.6" />
        </g>
      );
    case 'afro':
      return (
        <g fill={color}>
          <path d="M30 42 C29 28 38 21 50 21 C62 21 71 28 70 42 C66 37 60 35 50 35 C40 35 34 37 30 42 Z" />
          {[36, 43, 50, 57, 64].map((cx, i) => (
            <circle key={cx} cx={cx} cy={i % 4 === 0 ? 36.4 : 35} r="3.4" />
          ))}
          <path d="M38 27 C43 24 49 23.4 54 24" stroke={light} strokeWidth="1.6" strokeLinecap="round" fill="none" opacity="0.5" />
        </g>
      );
    case 'afro_puff':
      return (
        <g fill={color}>
          {!hat && (
            <>
              <circle cx="50" cy="14" r="10.5" />
              {[
                [41, 10],
                [46, 5],
                [54, 5],
                [59, 10],
              ].map(([cx, cy]) => (
                <circle key={cx} cx={cx} cy={cy} r="3.6" />
              ))}
              <rect x="44" y="21" width="12" height="3" rx="1.5" fill={shade(color, 0.35)} />
            </>
          )}
          <path d="M30 42 C29 28 38 23 50 23 C62 23 71 28 70 42 C66 36 60 33.5 50 33.5 C40 33.5 34 36 30 42 Z" />
          <path d="M38 28 C43 25.6 49 25 54 25.6" stroke={light} strokeWidth="1.4" strokeLinecap="round" fill="none" opacity="0.5" />
        </g>
      );
    case 'puffs':
      return (
        <g>
          <path d="M30 42 C29 28 38 22 50 22 C62 22 71 28 70 42 C66 36 60 33.5 50 33.5 C40 33.5 34 36 30 42 Z" fill={color} />
          <path d="M50 22.4 L50 33" stroke={shade(color, -0.3)} strokeWidth="0.9" />
          {shine('M39 27 C43 25 47 24.4 49 24.4')}
        </g>
      );
    case 'locs':
      return (
        <g>
          <path d="M29.5 44 C28 28 38 21 50 21 C62 21 72 28 70.5 44 C68 37 62 33 50 33 C38 33 32 37 29.5 44 Z" fill={color} />
          <g stroke={color} strokeWidth="4" strokeLinecap="round" fill="none">
            <path d="M34 33 C31.6 38 31.4 43 32.4 48" />
            <path d="M66 33 C68.4 38 68.6 43 67.6 48" />
          </g>
          <g stroke={shade(color, -0.3)} strokeWidth="0.7" opacity="0.7">
            <path d="M40 24.5 L40 33 M45 22.8 L45 32.6 M50 22.3 L50 32.5 M55 22.8 L55 32.6 M60 24.5 L60 33" />
          </g>
        </g>
      );
    case 'cornrows':
      return (
        <g>
          <path d="M30.5 43 C30 30 39 24.6 50 24.6 C61 24.6 70 30 69.5 43 C66 35.6 59 32.4 50 32.4 C41 32.4 34 35.6 30.5 43 Z" fill={color} />
          <g stroke={shade(color, 0.3)} strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.8">
            {[-14, -7, 0, 7, 14].map((dx) => (
              <path key={dx} d={`M${50 + dx} 33 Q${50 + dx * 0.9} 28 ${50 + dx * 0.55} 25`} />
            ))}
          </g>
        </g>
      );
    case 'long_curly':
      return (
        <g fill={color}>
          {[
            [34, 29, 6.5],
            [42, 24, 7],
            [50, 22.5, 7.5],
            [58, 24, 7],
            [66, 29, 6.5],
            [30.5, 37, 5],
            [69.5, 37, 5],
            [30, 45, 4.4],
            [70, 45, 4.4],
          ].map(([cx, cy, r]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r={r} />
          ))}
          <circle cx="47" cy="20" r="1.8" fill={light} opacity="0.6" />
        </g>
      );
    case 'pixie':
      return (
        <g>
          <path d="M29.5 46 C27 28 38 21 50 21 C62 21 72 27 70.5 42 C68 36 63 33.6 58 33.6 C52 38.4 42 40.4 33.6 38.4 C31.6 40.4 30.4 43 29.5 46 Z" fill={color} />
          {shine('M44 25 C49 23.4 55 23.4 60 25')}
        </g>
      );
    case 'bangs':
      return (
        <g>
          <path d="M29 52 C27 29 38 21 50 21 C62 21 73 29 71 52 C70 45 69.4 41 68.6 37.6 C60 38.6 40 38.6 31.4 37.6 C30.6 41 30 45 29 52 Z" fill={color} />
          <g stroke={shade(color, -0.25)} strokeWidth="0.7" opacity="0.6">
            <path d="M38 38 L39 31 M44 38.4 L44.6 30 M50 38.6 L50 30 M56 38.4 L55.4 30 M62 38 L61 31" />
          </g>
          {shine('M40 25.5 C44 24 50 23.4 55 24')}
        </g>
      );
    case 'space_buns':
      return (
        <g>
          {!hat && (
            <>
              <circle cx="35" cy="18" r="7" fill={shade(color, -0.1)} />
              <circle cx="65" cy="18" r="7" fill={shade(color, -0.1)} />
              <path d="M31 16 C33 13.6 37 13.6 39 16 M61 16 C63 13.6 67 13.6 69 16" stroke={light} strokeWidth="1.2" fill="none" opacity="0.6" />
            </>
          )}
          <path d="M29 44 C28 28 39 21 50 21 C61 21 72 28 71 44 C68 36 60 31 50 31 C40 31 32 36 29 44 Z" fill={color} />
          <path d="M50 21.4 L50 31" stroke={shade(color, -0.3)} strokeWidth="0.9" />
        </g>
      );
    case 'wavy':
      return (
        <g>
          <path d="M28 48 C26 28 38 20 50 20 C62 20 74 28 72 48 C70 40 66 36 62 32 C60 36 54 36 50 33 C44 37 36 36 34 34 C31 38 29 42 28 48 Z" fill={color} />
          {shine('M42 24.5 C46 23 52 22.6 57 23.6')}
        </g>
      );
    case 'bob':
      return (
        <g>
          <path d="M29 42 C29 27 39 21 50 21 C61 21 71 27 71 42 L71 37.5 C60 35 40 35 29 37.5 Z" fill={color} />
          <path d="M29 38 L29 62 C31 63 33 62 34 61 L34 38 Z M71 38 L71 62 C69 63 67 62 66 61 L66 38 Z" fill={color} />
          {shine('M40 25 C44 23.4 50 23 55 23.6')}
        </g>
      );
    case 'long':
    case 'braids':
      return (
        <g>
          <path d="M29 48 C28 29 38 21 50 21 C62 21 72 29 71 48 C69 39 62 30 50 28 C38 30 31 39 29 48 Z" fill={color} />
          <path d="M50 21.5 L50 28" stroke={shade(color, -0.3)} strokeWidth="0.9" />
          {shine('M40 25.5 C43 24 46 23.2 48 23')}
        </g>
      );
    case 'ponytail':
    case 'pigtails':
      return (
        <g>
          <path d="M29 44 C28 28 39 21 50 21 C61 21 72 28 71 44 C68 36 60 31 50 31 C40 31 32 36 29 44 Z" fill={color} />
          {shine('M40 25.5 C44 24 50 23.4 55 24')}
        </g>
      );
    case 'bun':
      return (
        <g>
          {!hat && (
            <>
              <circle cx="50" cy="16" r="8.5" fill={shade(color, -0.1)} />
              <path d="M44 14 C47 11 53 11 56 14" stroke={light} strokeWidth="1.4" fill="none" opacity="0.6" />
            </>
          )}
          <path d="M29 44 C28 28 39 21 50 21 C61 21 72 28 71 44 C68 36 60 31 50 31 C40 31 32 36 29 44 Z" fill={color} />
          {shine('M40 25.5 C44 24 50 23.4 55 24')}
        </g>
      );
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ *
 * Cara
 * ------------------------------------------------------------------ */

const Eyes: React.FC<{ kind: string; iris: string }> = ({ kind, iris }) => {
  // El iris va de su color y la pupila encima, en tinta: con el color por
  // defecto (casi negro) los ojos quedan como siempre.
  const dot = (cx: number) => (
    <g key={cx}>
      <ellipse cx={cx} cy="48" rx="2.7" ry="3.3" fill={iris} />
      <ellipse cx={cx} cy="48.3" rx="1.45" ry="1.85" fill={INK} />
      <circle cx={cx + 0.9} cy="46.8" r="1" fill="#FFFFFF" />
    </g>
  );
  const arc = (cx: number, up = true) => (
    <path
      key={cx}
      d={up ? `M${cx - 3.6} 49 Q${cx} 44.6 ${cx + 3.6} 49` : `M${cx - 3.6} 47.6 Q${cx} 51 ${cx + 3.6} 47.6`}
      stroke={INK}
      strokeWidth="2.2"
      strokeLinecap="round"
      fill="none"
    />
  );
  switch (kind) {
    case 'happy':
      return <g>{[42, 58].map((x) => arc(x))}</g>;
    case 'wink':
      return <g>{dot(42)}{arc(58)}</g>;
    case 'sleepy':
      return (
        <g>
          {[42, 58].map((x) => (
            <g key={x}>
              <path d={`M${x - 3} 48 A3 3 0 0 0 ${x + 3} 48 Z`} fill={INK} />
              <path d={`M${x - 3.6} 48 L${x + 3.6} 48`} stroke={INK} strokeWidth="1.6" strokeLinecap="round" />
            </g>
          ))}
        </g>
      );
    case 'surprised':
      return (
        <g>
          {[42, 58].map((x) => (
            <g key={x}>
              <circle cx={x} cy="48" r="4" fill="#FFFFFF" stroke={INK} strokeWidth="1" />
              <circle cx={x} cy="48" r="2.3" fill={iris} />
              <circle cx={x} cy="48" r="1.2" fill={INK} />
            </g>
          ))}
        </g>
      );
    case 'determined':
      return (
        <g>
          <path d="M39.2 46.4 L45 47.6 L45 49.6 A2.9 2.9 0 0 1 39.2 49.6 Z" fill={INK} />
          <path d="M60.8 46.4 L55 47.6 L55 49.6 A2.9 2.9 0 0 0 60.8 49.6 Z" fill={INK} />
          <circle cx="43.4" cy="48.8" r="0.8" fill="#FFFFFF" />
          <circle cx="59.4" cy="48.8" r="0.8" fill="#FFFFFF" />
        </g>
      );
    case 'stars':
      return (
        <g fill="#FACC15" stroke="#B45309" strokeWidth="0.6" strokeLinejoin="round">
          {[42, 58].map((x) => (
            <path
              key={x}
              d={`M${x} 43.6 L${x + 1.4} 46.6 L${x + 4.4} 46.9 L${x + 2.1} 49 L${x + 2.8} 52 L${x} 50.4 L${x - 2.8} 52 L${x - 2.1} 49 L${x - 4.4} 46.9 L${x - 1.4} 46.6 Z`}
            />
          ))}
        </g>
      );
    case 'hearts':
      return (
        <g fill="#EF4444">
          {[42, 58].map((x) => (
            <path
              key={x}
              d={`M${x} 51.6 C${x - 5} 48 ${x - 4.2} 44 ${x - 1.8} 44.4 C${x - 0.8} 44.6 ${x} 45.6 ${x} 46.4 C${x} 45.6 ${x + 0.8} 44.6 ${x + 1.8} 44.4 C${x + 4.2} 44 ${x + 5} 48 ${x} 51.6 Z`}
            />
          ))}
        </g>
      );
    default:
      return <g>{dot(42)}{dot(58)}</g>;
  }
};

/**
 * Pestañas en el borde exterior de cada ojo. Siguen la altura del párpado de
 * cada mirada: en los ojos cerrados (feliz, guiño) salen del arco.
 */
const Lashes: React.FC<{ kind: string; eyes: string }> = ({ kind, eyes }) => {
  if (kind === 'none') return null;
  const lidY = eyes === 'surprised' ? 44 : eyes === 'sleepy' || eyes === 'happy' ? 46.6 : 45;
  // A 40 px una pestaña muy fina no se ve, pero si es larga se mete en la
  // ceja: van gruesas, cortas y abiertas hacia el rabillo.
  const len = kind === 'natural' ? 1.8 : 2.6;
  const w = kind === 'dramatic' ? 1.5 : 1.2;
  const side = (cx: number, dir: -1 | 1) => {
    // dir -1: ojo izquierdo (pestañas hacia la izquierda); 1: derecho.
    const ox = cx + dir * 2.6;
    const strands = [
      [cx + dir * 2.7, lidY + 2.2, dir * len, -len * 0.35],
      [cx + dir * 2.2, lidY + 0.8, dir * len * 0.85, -len * 0.75],
      [cx + dir * 1.2, lidY, dir * len * 0.5, -len * 0.95],
    ];
    return (
      <g key={cx}>
        {kind === 'dramatic' && (
          <path
            d={`M${cx - dir * 3.2} ${lidY + 0.6} Q${cx} ${lidY - 1.2} ${ox} ${lidY + 1.2} L${ox + dir * 2.4} ${lidY - 0.4}`}
            stroke={INK}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
        )}
        {strands.map(([x, y, dx, dy], i) => (
          <path key={i} d={`M${x} ${y} l${dx} ${dy}`} />
        ))}
      </g>
    );
  };
  return (
    <g stroke={INK} strokeWidth={w} strokeLinecap="round" fill="none">
      {side(42, -1)}
      {side(58, 1)}
    </g>
  );
};

/** Pendientes en el lóbulo de cada oreja. */
const Earrings: React.FC<{ kind: string; color: string }> = ({ kind, color }) => {
  const GOLD = '#F5C542';
  const GOLD_DARK = '#B7862A';
  const at = (x: number) => {
    switch (kind) {
      case 'studs':
        return <circle key={x} cx={x} cy="54.6" r="1.3" fill={GOLD} stroke={GOLD_DARK} strokeWidth="0.4" />;
      case 'hoops':
        return <circle key={x} cx={x} cy="57.6" r="3" fill="none" stroke={GOLD} strokeWidth="1.1" />;
      case 'drops':
        return (
          <g key={x}>
            <path d={`M${x} 54.4 L${x} 57.4`} stroke={GOLD} strokeWidth="0.8" />
            <path d={`M${x} 57 C${x - 2} 59.6 ${x - 1.6} 62 ${x} 62 C${x + 1.6} 62 ${x + 2} 59.6 ${x} 57 Z`} fill={color} stroke={shade(color, -0.3)} strokeWidth="0.4" />
          </g>
        );
      case 'stars':
        return (
          <path
            key={x}
            d={`M${x} 53 L${x + 0.8} 54.8 L${x + 2.6} 55 L${x + 1.2} 56.2 L${x + 1.6} 58 L${x} 57 L${x - 1.6} 58 L${x - 1.2} 56.2 L${x - 2.6} 55 L${x - 0.8} 54.8 Z`}
            fill={GOLD}
            stroke={GOLD_DARK}
            strokeWidth="0.35"
            strokeLinejoin="round"
          />
        );
      default:
        return null;
    }
  };
  if (kind === 'none') return null;
  return <g>{at(30.2)}{at(69.8)}</g>;
};

const Eyebrows: React.FC<{ kind: string; color: string; eyes: string }> = ({ kind, color, eyes }) => {
  if (kind === 'none') return null;
  // Con ojos de sorpresa las cejas suben un poco: si no, se pisan.
  const y = eyes === 'surprised' ? 40.5 : 42;
  const paths: Record<string, [string, string]> = {
    natural: [`M38 ${y} Q42 ${y - 2.2} 46 ${y - 0.4}`, `M54 ${y - 0.4} Q58 ${y - 2.2} 62 ${y}`],
    raised: [`M38 ${y - 1} Q42 ${y - 5} 46 ${y - 2}`, `M54 ${y - 2} Q58 ${y - 5} 62 ${y - 1}`],
    angry: [`M38 ${y - 2.2} L46 ${y + 0.8}`, `M54 ${y + 0.8} L62 ${y - 2.2}`],
    worried: [`M38 ${y + 0.4} L46 ${y - 2.6}`, `M54 ${y - 2.6} L62 ${y + 0.4}`],
    thick: [`M37.6 ${y} Q42 ${y - 2.6} 46.4 ${y - 0.6}`, `M53.6 ${y - 0.6} Q58 ${y - 2.6} 62.4 ${y}`],
  };
  const [l, r] = paths[kind] ?? paths.natural;
  const w = kind === 'thick' ? 3.4 : 1.9;
  return (
    <g stroke={color} strokeWidth={w} strokeLinecap="round" fill="none">
      <path d={l} />
      <path d={r} />
    </g>
  );
};

const Mouth: React.FC<{ kind: string; lip: string | null }> = ({ kind, lip }) => {
  // Con los labios pintados el trazo de la boca toma ese color y engorda un
  // poco, que es como se lee "pintalabios" a este tamaño.
  const INK = lip ?? INK_LINE;
  const lipFill = lip ? <LipShape kind={kind} color={lip} /> : null;
  switch (kind) {
    case 'grin':
      return (
        <g>
          <path d="M42.5 57.5 Q50 66.5 57.5 57.5 Z" fill="#FFFFFF" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M44 58.8 L56 58.8" stroke="#CBD5E1" strokeWidth="0.8" />
        </g>
      );
    case 'joy':
      return (
        <g>
          <path d="M42 56.8 Q50 68 58 56.8 Z" fill="#7F1D1D" stroke={INK} strokeWidth="1.6" strokeLinejoin="round" />
          <path d="M43.6 57.6 L56.4 57.6 L55.6 59 L44.4 59 Z" fill="#FFFFFF" />
          <path d="M45.5 63.6 Q50 60.4 54.5 63.6 Q50 66.2 45.5 63.6 Z" fill="#F87171" />
        </g>
      );
    case 'smirk':
      return (
        <g>
          {lipFill}
          <path d="M44 60 Q51 61.6 57 56.8" stroke={INK} strokeWidth={lip ? 2.6 : 2} strokeLinecap="round" fill="none" />
        </g>
      );
    case 'open':
      return (
        <g>
          <ellipse cx="50" cy="60" rx="3.6" ry="4.4" fill="#7F1D1D" stroke={INK} strokeWidth="1.4" />
          <ellipse cx="50" cy="62" rx="2" ry="1.4" fill="#F87171" />
        </g>
      );
    case 'tongue':
      return (
        <g>
          <path d="M53 59.4 Q54 65.6 50.6 65 Q48 64.4 48.6 60 Z" fill="#F87171" stroke={INK} strokeWidth="1" />
          <path d="M43 57.6 Q50 63.4 57 57.6" stroke={INK} strokeWidth="2" strokeLinecap="round" fill="none" />
        </g>
      );
    case 'neutral':
      return (
        <g>
          {lipFill}
          <path d="M45 59.4 L55 59.4" stroke={INK} strokeWidth={lip ? 2.6 : 2} strokeLinecap="round" />
        </g>
      );
    case 'cat':
      return <path d="M43.6 58 Q46.8 61.6 50 58.4 Q53.2 61.6 56.4 58" stroke={INK} strokeWidth={lip ? 2.4 : 1.9} strokeLinecap="round" strokeLinejoin="round" fill="none" />;
    default:
      return (
        <g>
          {lipFill}
          <path d="M43.5 57.6 Q50 63.6 56.5 57.6" stroke={INK} strokeWidth={lip ? 2.8 : 2.1} strokeLinecap="round" fill="none" />
        </g>
      );
  }
};

/** Relleno de labios para las bocas cerradas, debajo del trazo. */
const LipShape: React.FC<{ kind: string; color: string }> = ({ kind, color }) => {
  if (kind !== 'smile' && kind !== 'neutral' && kind !== 'smirk' && kind !== 'cat') return null;
  return (
    <path
      d="M44 58 Q47 56.4 50 57.6 Q53 56.4 56 58 Q50 63 44 58 Z"
      fill={color}
      opacity="0.85"
    />
  );
};

const FacialHair: React.FC<{ kind: string; color: string }> = ({ kind, color }) => {
  const mustache = (
    <path d="M42.6 57 C44 53.8 47.6 53.4 50 55.2 C52.4 53.4 56 53.8 57.4 57 C55 56.2 52.6 56.6 50 57 C47.4 56.6 45 56.2 42.6 57 Z" fill={color} />
  );
  switch (kind) {
    case 'stubble':
      return (
        <path
          d="M31 50 C31 62 39 69 50 69 C61 69 69 62 69 50 C67 57 63 61 58 62 C55 58 45 58 42 62 C37 61 33 57 31 50 Z"
          fill={color}
          opacity="0.22"
        />
      );
    case 'mustache':
      return mustache;
    case 'goatee':
      return (
        <g>
          {mustache}
          <path d="M45.4 63.4 Q50 62 54.6 63.4 L53.4 68 Q50 69.4 46.6 68 Z" fill={color} />
        </g>
      );
    case 'beard':
      return (
        <g>
          <path
            d="M30.4 48 C29.6 64 38 72.6 50 72.6 C62 72.6 70.4 64 69.6 48 C68 56 64.6 60.6 60 61.6 C57 57.4 43 57.4 40 61.6 C35.4 60.6 32 56 30.4 48 Z"
            fill={color}
          />
          {mustache}
        </g>
      );
    default:
      return null;
  }
};

const Detail: React.FC<{ kind: string; skin: string; accent: string }> = ({ kind, skin, accent }) => {
  switch (kind) {
    case 'blush':
      return (
        <g fill="#F43F5E" opacity="0.32">
          <ellipse cx="36" cy="55" rx="4.2" ry="2.6" />
          <ellipse cx="64" cy="55" rx="4.2" ry="2.6" />
        </g>
      );
    case 'freckles':
      return (
        <g fill={shade(skin, -0.38)} opacity="0.75">
          {[[35, 53], [38.4, 55], [34.6, 56.6], [65, 53], [61.6, 55], [65.4, 56.6]].map(([cx, cy]) => (
            <circle key={`${cx}-${cy}`} cx={cx} cy={cy} r="0.85" />
          ))}
        </g>
      );
    case 'mole':
      return <circle cx="58.6" cy="62.4" r="1" fill={shade(skin, -0.55)} />;
    case 'paint':
      return (
        <g stroke={accent} strokeWidth="1.8" strokeLinecap="round">
          <path d="M33 53 L39 54" />
          <path d="M33.4 56.4 L39.4 57.2" />
          <path d="M67 53 L61 54" />
          <path d="M66.6 56.4 L60.6 57.2" />
        </g>
      );
    case 'scar':
      return (
        <g stroke={shade(skin, -0.3)} strokeLinecap="round">
          <path d="M60 41 L64 56" strokeWidth="1.3" />
          <path d="M60 45.4 L63.6 44.4 M61 49.4 L64.6 48.4 M62 53.4 L65.4 52.4" strokeWidth="0.8" />
        </g>
      );
    case 'bandage':
      return (
        <g transform="rotate(-25 36 56)">
          <rect x="30.6" y="53.6" width="11" height="4.8" rx="2.2" fill="#F5D0A9" stroke="#D6A774" strokeWidth="0.5" />
          <rect x="34.4" y="53.6" width="3.4" height="4.8" fill="#E7B98A" />
        </g>
      );
    default:
      return null;
  }
};

const Glasses: React.FC<{ kind: string }> = ({ kind }) => {
  switch (kind) {
    case 'round':
      return (
        <g fill="#FFFFFF" fillOpacity="0.18" stroke="#3F2A1D" strokeWidth="1.4">
          <circle cx="42" cy="48" r="5.6" />
          <circle cx="58" cy="48" r="5.6" />
          <path d="M47.6 47.4 Q50 45.8 52.4 47.4 M36.4 47 L31 45.6 M63.6 47 L69 45.6" fill="none" />
        </g>
      );
    case 'square':
      return (
        <g stroke="#111827" strokeWidth="1.8" fill="#FFFFFF" fillOpacity="0.15">
          <rect x="35" y="43.4" width="13" height="9.4" rx="2" />
          <rect x="52" y="43.4" width="13" height="9.4" rx="2" />
          <path d="M48 46.6 L52 46.6 M35 45.6 L31 44.6 M65 45.6 L69 44.6" fill="none" />
        </g>
      );
    case 'sunglasses':
      return (
        <g>
          <path d="M34 44 L48 44 L47 50.6 Q46 53.4 41 53.4 Q35.4 53.4 34.6 50.6 Z" fill="#0F172A" />
          <path d="M52 44 L66 44 L65.4 50.6 Q64.6 53.4 59 53.4 Q54 53.4 53 50.6 Z" fill="#0F172A" />
          <path d="M31 44.6 L69 44.6" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" />
          <path d="M37 46.4 L40 46.4 M55 46.4 L58 46.4" stroke="#FFFFFF" strokeWidth="1.2" strokeLinecap="round" opacity="0.55" />
        </g>
      );
    case 'star':
      return (
        <g fill="#EC4899" stroke="#9D174D" strokeWidth="0.8" strokeLinejoin="round">
          {[42, 58].map((x) => (
            <path
              key={x}
              d={`M${x} 41 L${x + 2.3} 45.6 L${x + 7.2} 46.2 L${x + 3.6} 49.6 L${x + 4.6} 54.6 L${x} 52.2 L${x - 4.6} 54.6 L${x - 3.6} 49.6 L${x - 7.2} 46.2 L${x - 2.3} 45.6 Z`}
            />
          ))}
          <path d="M48 47 L52 47" stroke="#9D174D" strokeWidth="1.4" />
          <circle cx="42" cy="48" r="1.6" fill="#1B1F2A" stroke="none" />
          <circle cx="58" cy="48" r="1.6" fill="#1B1F2A" stroke="none" />
        </g>
      );
    case 'visor':
      return (
        <g>
          <rect x="31" y="43" width="38" height="9" rx="4.5" fill="#22D3EE" fillOpacity="0.85" stroke="#0E7490" strokeWidth="1.2" />
          <path d="M35 46 L50 46" stroke="#FFFFFF" strokeWidth="1.3" strokeLinecap="round" opacity="0.8" />
        </g>
      );
    case 'monocle':
      return (
        <g fill="none" stroke="#B45309" strokeWidth="1.3">
          <circle cx="58" cy="48" r="5.4" fill="#FFFFFF" fillOpacity="0.2" />
          <path d="M62.4 51.6 Q66 60 62 68" strokeWidth="0.7" />
        </g>
      );
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ *
 * Ropa
 * ------------------------------------------------------------------ */

const SHOULDERS = 'M14 100 C14 86 24 78 38 76 L62 76 C76 78 86 86 86 100 Z';

const Body: React.FC<{ kind: string; color: string; skin: string; skinShadow: string }> = ({
  kind,
  color,
  skin,
  skinShadow,
}) => {
  const dark = shade(color, -0.22);
  const trim = isLight(color) ? shade(color, -0.35) : shade(color, 0.45);
  const neck = (
    <g>
      <rect x="43.5" y="62" width="13" height="17" rx="4" fill={skin} />
      <path d="M43.5 66 Q50 72 56.5 66 L56.5 70 Q50 75 43.5 70 Z" fill={skinShadow} />
    </g>
  );
  switch (kind) {
    case 'hoodie':
      return (
        <g>
          <path d="M30 80 C30 70 38 68 50 68 C62 68 70 70 70 80 Z" fill={dark} />
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M36 77 Q50 90 64 77 Q60 75 50 75 Q40 75 36 77 Z" fill={dark} />
          <path d="M45 84 L44 93 M55 84 L56 93" stroke="#F8FAFC" strokeWidth="1.3" strokeLinecap="round" />
          <circle cx="44" cy="93.6" r="1.1" fill="#F8FAFC" />
          <circle cx="56" cy="93.6" r="1.1" fill="#F8FAFC" />
          <path d="M36 100 L39 94 L61 94 L64 100" fill={dark} opacity="0.5" />
        </g>
      );
    case 'sweater':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M40 76.6 Q50 84 60 76.6" stroke={dark} strokeWidth="3" fill="none" strokeLinecap="round" />
          <path d="M20 90 L26 86 L32 90 L38 86 L44 90 L50 86 L56 90 L62 86 L68 90 L74 86 L80 90" stroke={trim} strokeWidth="2" fill="none" strokeLinejoin="round" />
          <path d="M18 96 L82 96" stroke={trim} strokeWidth="1.4" opacity="0.6" />
        </g>
      );
    case 'turtleneck':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M42 64.6 L58 64.6 L59 79 Q50 82 41 79 Z" fill={dark} />
          <path d="M42 68.6 L58 68.6 M41.6 72.6 L58.4 72.6 M41.3 76.4 L58.7 76.4" stroke={shade(color, -0.35)} strokeWidth="0.8" />
        </g>
      );
    case 'jersey':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M42 76 L50 86 L58 76" fill={skin} />
          <path d="M42 76 L50 86 L58 76" fill="none" stroke={trim} strokeWidth="2.2" strokeLinejoin="round" />
          <path d="M24 82 L30 100 M76 82 L70 100" stroke={trim} strokeWidth="3" />
          <text x="50" y="98.6" textAnchor="middle" fontSize="10" fontWeight="800" fontFamily="Outfit, sans-serif" fill={trim}>
            7
          </text>
        </g>
      );
    case 'overalls':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill="#F8FAFC" />
          <path d="M42 76 Q50 80 58 76" stroke="#CBD5E1" strokeWidth="1.2" fill="none" />
          <path d="M33 100 L35 86 L65 86 L67 100 Z" fill={color} />
          <path d="M34 77.6 L37 88 M66 77.6 L63 88" stroke={color} strokeWidth="4" strokeLinecap="round" />
          <circle cx="37.2" cy="88" r="1.5" fill="#FACC15" />
          <circle cx="62.8" cy="88" r="1.5" fill="#FACC15" />
          <rect x="45" y="89" width="10" height="7" rx="1.4" fill={dark} />
        </g>
      );
    case 'suit':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M41 76 L50 100 L59 76 Z" fill="#F8FAFC" />
          <path d="M50 79 L47.8 82 L50 96 L52.2 82 Z" fill={isLight(color) ? '#1E293B' : '#DC2626'} />
          <path d="M41 76 L50 100 L37 86 L40 82 Z M59 76 L50 100 L63 86 L60 82 Z" fill={dark} />
          <rect x="66" y="88" width="7" height="1.6" rx="0.8" fill={trim} />
        </g>
      );
    case 'labcoat':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill="#F1F5F9" />
          <path d="M42 76 L50 92 L58 76 Z" fill={color} />
          <path d="M42 76 L50 100 M58 76 L50 100" stroke="#CBD5E1" strokeWidth="1.2" />
          <path d="M42 76 L38 84 L44 86 Z M58 76 L62 84 L56 86 Z" fill="#E2E8F0" />
          <rect x="62" y="89" width="8" height="8" rx="1" fill="#E2E8F0" stroke="#CBD5E1" strokeWidth="0.6" />
          <path d="M64.4 86 L64.4 91" stroke="#2563EB" strokeWidth="1.4" strokeLinecap="round" />
        </g>
      );
    case 'kimono':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M40 76 L58 100 L52 100 L37 79 Z" fill="#F8FAFC" />
          <path d="M60 76 L46 94 L42 90 L56 76 Z" fill={trim} />
          <rect x="16" y="92" width="68" height="8" fill={dark} />
          <path d="M16 93.4 L84 93.4" stroke={trim} strokeWidth="0.8" />
        </g>
      );
    case 'hero':
      return (
        <g>
          <path d="M18 100 L24 80 L38 75 L62 75 L76 80 L82 100 Z" fill="#DC2626" />
          {neck}
          <path d="M20 100 C20 88 28 80 40 77 L60 77 C72 80 80 88 80 100 Z" fill={color} />
          <path d="M50 82 L57 88 L50 97 L43 88 Z" fill="#FACC15" stroke={dark} strokeWidth="1" />
          <path d="M47 88 L53 88" stroke={dark} strokeWidth="1.2" />
        </g>
      );
    case 'armor':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill="#94A3B8" />
          <path d="M36 78 C40 76 60 76 64 78 L66 100 L34 100 Z" fill="#CBD5E1" />
          <path d="M50 78 L50 100" stroke="#94A3B8" strokeWidth="1.2" />
          <ellipse cx="22" cy="86" rx="10" ry="7" fill="#64748B" />
          <ellipse cx="78" cy="86" rx="10" ry="7" fill="#64748B" />
          <ellipse cx="22" cy="85" rx="7" ry="4" fill="#94A3B8" />
          <ellipse cx="78" cy="85" rx="7" ry="4" fill="#94A3B8" />
          <path d="M42 77 Q50 80 58 77 L57 80 Q50 83 43 80 Z" fill={color} />
          <path d="M44 90 L50 94 L56 90" stroke={color} strokeWidth="2.4" fill="none" strokeLinecap="round" />
        </g>
      );
    case 'astronaut':
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill="#F1F5F9" />
          <path d="M38 76 Q50 82 62 76 Q62 72 50 72 Q38 72 38 76 Z" fill="#94A3B8" />
          <path d="M38 76 Q50 82 62 76" stroke="#64748B" strokeWidth="1.4" fill="none" />
          <rect x="40" y="85" width="20" height="11" rx="2" fill={color} />
          <circle cx="45" cy="90.4" r="1.6" fill="#FACC15" />
          <circle cx="50" cy="90.4" r="1.6" fill="#EF4444" />
          <circle cx="55" cy="90.4" r="1.6" fill="#22C55E" />
          <circle cx="24" cy="88" r="3.4" fill={color} />
        </g>
      );
    default: // camiseta
      return (
        <g>
          {neck}
          <path d={SHOULDERS} fill={color} />
          <path d="M41.6 76 Q50 83 58.4 76" fill={skin} />
          <path d="M41.6 76 Q50 83 58.4 76" fill="none" stroke={dark} strokeWidth="2" strokeLinecap="round" />
          <path d="M26 86 Q27 94 25 100 M74 86 Q73 94 75 100" stroke={dark} strokeWidth="1" fill="none" opacity="0.6" />
        </g>
      );
  }
};

/* ------------------------------------------------------------------ *
 * Accesorios de cabeza
 * ------------------------------------------------------------------ */

const Halo: React.FC<{ back?: boolean }> = ({ back }) =>
  back ? (
    <ellipse cx="50" cy="14" rx="15" ry="4.6" fill="none" stroke="#FACC15" strokeWidth="2.8" opacity="0.95" />
  ) : null;

const Accessory: React.FC<{ kind: string; color: string; hair: string }> = ({ kind, color }) => {
  const dark = shade(color, -0.25);
  const light = shade(color, 0.3);
  switch (kind) {
    case 'hijab':
      // Un marco alrededor de la cara que baja hasta los hombros. El hueco de
      // la cara se recorta con evenodd para no tapar los rasgos.
      return (
        <g>
          <path
            fillRule="evenodd"
            d="M50 18 C67 18 77 30 77 48 C77 62 73 72 67 80 L72 100 L28 100 L33 80 C27 72 23 62 23 48 C23 30 33 18 50 18 Z M32.6 49 A17.4 19.6 0 1 0 67.4 49 A17.4 19.6 0 1 0 32.6 49 Z"
            fill={color}
          />
          <path d="M32.6 49 A17.4 19.6 0 0 1 67.4 49" fill="none" stroke={dark} strokeWidth="0.9" opacity="0.5" />
          <path d="M36 74 C42 79 58 79 64 74 M38 84 C44 88 56 88 62 84" fill="none" stroke={dark} strokeWidth="0.9" opacity="0.45" />
        </g>
      );
    case 'headwrap':
      return (
        <g>
          <path d="M27.4 39 C25 22 36 12 50 12 C64 12 75 22 72.6 39 C66 33.4 58 31.4 50 31.4 C42 31.4 34 33.4 27.4 39 Z" fill={color} />
          <path d="M30 32 C38 24 62 24 70 32 M33 25 C42 19 58 19 67 25" fill="none" stroke={dark} strokeWidth="0.9" opacity="0.55" />
          <ellipse cx="44.6" cy="15" rx="5" ry="3.6" fill={light} />
          <ellipse cx="55.4" cy="15" rx="5" ry="3.6" fill={light} />
          <circle cx="50" cy="16.6" r="3.2" fill={dark} />
        </g>
      );
    case 'headband':
      return (
        <g>
          <path d="M30.4 38 C35 26 65 26 69.6 38" fill="none" stroke={color} strokeWidth="3.4" strokeLinecap="round" />
          <path d="M33 33.6 C39 27.8 61 27.8 67 33.6" fill="none" stroke={light} strokeWidth="0.8" opacity="0.6" />
        </g>
      );
    case 'cap':
      return (
        <g>
          <path d="M28 36 C27 18 73 18 72 36 Z" fill={color} />
          <path d="M28 36 C40 32 60 32 72 36 C74 40 84 40 86 38 C84 34 78 33 72 33" fill={dark} />
          <path d="M50 18.6 L50 34" stroke={dark} strokeWidth="0.9" />
          <circle cx="50" cy="18.8" r="1.6" fill={dark} />
        </g>
      );
    case 'beanie':
      return (
        <g>
          <path d="M28.6 36 C28 16 72 16 71.4 36 Z" fill={color} />
          <path d="M37 21.4 L37 34 M43.4 19 L43.4 34 M50 18 L50 34 M56.6 19 L56.6 34 M63 21.4 L63 34" stroke={dark} strokeWidth="0.9" opacity="0.7" />
          <rect x="27" y="31.6" width="46" height="7.4" rx="3.4" fill={dark} />
          <circle cx="50" cy="14.6" r="5" fill={light} />
        </g>
      );
    case 'headphones':
      return (
        <g>
          <path d="M28 46 C26 18 74 18 72 46" fill="none" stroke="#1E293B" strokeWidth="3.6" strokeLinecap="round" />
          <rect x="23.4" y="40" width="8.6" height="15" rx="4.2" fill={color} />
          <rect x="68" y="40" width="8.6" height="15" rx="4.2" fill={color} />
          <rect x="25" y="43" width="3" height="9" rx="1.5" fill={light} opacity="0.7" />
        </g>
      );
    case 'bandana':
      return (
        <g>
          <path d="M29.4 35.6 C38 30.4 62 30.4 70.6 35.6 L70 41 C62 36.6 38 36.6 30 41 Z" fill={color} />
          <path d="M70 36 L78 32 L77 39 Z M70 38 L77 44 L73 45 Z" fill={dark} />
          <circle cx="40" cy="36" r="0.9" fill="#FFFFFF" />
          <circle cx="50" cy="34.6" r="0.9" fill="#FFFFFF" />
          <circle cx="60" cy="36" r="0.9" fill="#FFFFFF" />
        </g>
      );
    case 'bow':
      return (
        <g>
          <path d="M60 24 L50 16 L50 30 Z" fill={color} />
          <path d="M60 24 L70 16 L70 30 Z" fill={color} />
          <path d="M60 24 L52 18 L52 28 Z M60 24 L68 18 L68 28 Z" fill={dark} opacity="0.35" />
          <circle cx="60" cy="23.4" r="3" fill={dark} />
        </g>
      );
    case 'flower':
      return (
        <g transform="translate(66 27)">
          {[0, 72, 144, 216, 288].map((r) => (
            <ellipse key={r} cx="0" cy="-4.4" rx="3" ry="4.6" fill={color} transform={`rotate(${r})`} />
          ))}
          <circle r="2.8" fill="#FACC15" />
          <path d="M-7 5 Q-10 2 -8 -1 Q-5 1 -7 5 Z" fill="#22C55E" />
        </g>
      );
    case 'crown':
      return (
        <g>
          <path d="M34 28 L32 12 L41 20 L50 9 L59 20 L68 12 L66 28 Z" fill="#FACC15" stroke="#B45309" strokeWidth="1.2" strokeLinejoin="round" />
          <rect x="33.6" y="25" width="32.8" height="4.6" rx="1" fill="#F59E0B" stroke="#B45309" strokeWidth="1" />
          <circle cx="50" cy="20.4" r="2.2" fill="#EF4444" />
          <circle cx="40" cy="22.6" r="1.5" fill="#3B82F6" />
          <circle cx="60" cy="22.6" r="1.5" fill="#10B981" />
        </g>
      );
    case 'wizard':
      return (
        <g>
          <path d="M50 1 C46 10 36 22 30 31 L70 31 C66 22 58 14 60 6 C56 8 52 5 50 1 Z" fill={color} />
          <ellipse cx="50" cy="31" rx="25" ry="5" fill={dark} />
          <path d="M44 16 L45 19 L48 19.4 L45.6 21 L46.4 24 L44 22.2 L41.6 24 L42.4 21 L40 19.4 L43 19 Z" fill="#FACC15" />
          <circle cx="56" cy="22" r="1.3" fill="#FACC15" />
          <circle cx="52" cy="12" r="1" fill="#FACC15" />
        </g>
      );
    case 'party_hat':
      return (
        <g transform="rotate(12 56 22)">
          <path d="M56 3 L44 30 L68 30 Z" fill={color} />
          <path d="M50.6 15.6 L61.4 15.6 M47.6 22.6 L64.4 22.6" stroke="#FFFFFF" strokeWidth="2" opacity="0.8" />
          <circle cx="56" cy="3" r="3" fill="#FACC15" />
        </g>
      );
    case 'graduation':
      return (
        <g>
          <path d="M34 28 C34 20 66 20 66 28 L66 33 L34 33 Z" fill="#1E293B" />
          <path d="M50 11 L80 20 L50 29 L20 20 Z" fill="#0F172A" />
          <circle cx="50" cy="20" r="1.4" fill={color} />
          <path d="M50 20 L73 23 L73 34" stroke={color} strokeWidth="1.2" fill="none" />
          <path d="M71.4 34 L74.6 34 L73.8 39 L72.2 39 Z" fill={color} />
        </g>
      );
    case 'viking':
      return (
        <g>
          <path d="M31 30 C24 26 20 16 23 8 C26 15 30 20 36 22 Z" fill="#F5E6C8" stroke="#C8B48A" strokeWidth="0.8" />
          <path d="M69 30 C76 26 80 16 77 8 C74 15 70 20 64 22 Z" fill="#F5E6C8" stroke="#C8B48A" strokeWidth="0.8" />
          <path d="M28.4 36 C27.6 16 72.4 16 71.6 36 Z" fill="#94A3B8" />
          <rect x="27" y="32" width="46" height="5.4" rx="2.4" fill="#64748B" />
          <path d="M50 17.6 L50 32" stroke="#64748B" strokeWidth="3" />
          {[33, 41, 59, 67].map((x) => (
            <circle key={x} cx={x} cy="34.7" r="0.9" fill="#CBD5E1" />
          ))}
        </g>
      );
    case 'cat_ears':
      return (
        <g>
          <path d="M29 34 L30 14 L44 25 Z M71 34 L70 14 L56 25 Z" fill={color} />
          <path d="M31.4 29 L32 18.6 L40 25 Z M68.6 29 L68 18.6 L60 25 Z" fill="#F9A8D4" />
          <path d="M29 34 Q50 20 71 34" stroke={color} strokeWidth="2.6" fill="none" />
        </g>
      );
    case 'horns':
      return (
        <g fill={color} stroke={dark} strokeWidth="0.8">
          <path d="M34 30 C30 24 30 17 33 12 C35 19 38 23 41 26 Z" />
          <path d="M66 30 C70 24 70 17 67 12 C65 19 62 23 59 26 Z" />
        </g>
      );
    case 'antenna':
      return (
        <g>
          <path d="M43 26 Q40 16 34 10 M57 26 Q60 16 66 10" stroke="#1E293B" strokeWidth="1.4" fill="none" strokeLinecap="round" />
          <circle cx="34" cy="10" r="3.4" fill={color} />
          <circle cx="66" cy="10" r="3.4" fill={color} />
          <circle cx="33" cy="9" r="1" fill="#FFFFFF" opacity="0.8" />
          <circle cx="65" cy="9" r="1" fill="#FFFFFF" opacity="0.8" />
        </g>
      );
    case 'halo':
      // El aro de atrás ya está dibujado; aquí solo el brillo de delante.
      return <path d="M36 16 Q50 21 64 16" stroke="#FEF08A" strokeWidth="1.6" fill="none" strokeLinecap="round" />;
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ *
 * Avatar aleatorio
 *
 * Se usa al crear una cuenta y como punto de partida de los invitados. Los
 * rasgos llamativos (barba, gafas, marcas) salen con menos frecuencia para
 * que el resultado parezca una persona y no una lotería de piezas.
 * ------------------------------------------------------------------ */

const pick = <T,>(list: readonly T[]): T => list[Math.floor(Math.random() * list.length)];
const sometimes = <T,>(chance: number, list: readonly T[], fallback: T): T =>
  Math.random() < chance ? pick(list) : fallback;

const ids = <T extends { id: string }>(list: readonly T[]) => list.map((o) => o.id as T['id']);

export function randomAvatar(): AvatarConfig {
  const humanSkin = SKIN_COLORS.slice(0, 8);
  return {
    skinColor: Math.random() < 0.9 ? pick(humanSkin) : pick(SKIN_COLORS),
    hairStyle: pick(ids(HAIR_STYLES)),
    hairColor: Math.random() < 0.75 ? pick(HAIR_COLORS.slice(0, 8)) : pick(HAIR_COLORS),
    eyes: Math.random() < 0.7 ? pick(['normal', 'happy', 'wink', 'determined'] as const) : pick(ids(EYES)),
    eyeColor: Math.random() < 0.85 ? pick(EYE_COLORS.slice(0, 6)) : pick(EYE_COLORS),
    lashes: sometimes(0.4, ids(LASHES).filter((l) => l !== 'none'), 'none'),
    lipstick: sometimes(0.25, ids(LIPSTICK).filter((l) => l !== 'none'), 'none'),
    earrings: sometimes(0.3, ids(EARRINGS).filter((e) => e !== 'none'), 'none'),
    eyebrows: pick(ids(EYEBROWS).filter((e) => e !== 'none')),
    mouth: pick(ids(MOUTHS)),
    facialHair: sometimes(0.2, ids(FACIAL_HAIR).filter((f) => f !== 'none'), 'none'),
    glasses: sometimes(0.25, ids(GLASSES).filter((g) => g !== 'none'), 'none'),
    detail: sometimes(0.3, ids(FACE_DETAILS).filter((d) => d !== 'none'), 'none'),
    accessory: sometimes(0.55, ids(ACCESSORIES).filter((x) => x !== 'none'), 'none'),
    accessoryColor: pick(OUTFIT_COLORS),
    clothing: pick(ids(CLOTHING)),
    clothingColor: pick(OUTFIT_COLORS),
    background: pick(ids(BACKGROUNDS)),
  };
}

